import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Briefcase, MapPin, Clock, Check } from 'lucide-react';
import Reveal from '../components/Reveal';
import { CAREERS, COMPANY } from '../data/mock';
import { submitApplication } from '../lib/api';

export default function Careers() {
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', cover: '', role: '' });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const apply = (role) => {
    setActive(role);
    setForm({ ...form, role: role.title });
    setTimeout(() => document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth' }), 50);
  };
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await submitApplication(form); setSent(true); } catch {} finally { setSubmitting(false); }
  };

  return (
    <div className="bg-[var(--bg)]">
      <section className="pt-36 pb-16 border-b border-[var(--line)]">
        <div className="container-rs">
          <span className="section-index"><span className="coral-dot" /> Careers</span>
          <Reveal>
            <h1 className="font-display text-7xl md:text-[150px] leading-[0.92] mt-6">Join the <span className="font-display-italic text-[var(--coral)]">studio.</span></h1>
          </Reveal>
          <div className="grid md:grid-cols-12 gap-8 mt-10">
            <p className="md:col-span-7 md:col-start-6 text-[18px] md:text-[20px] text-[var(--ink-mute)] leading-relaxed">
              At Bespoke, our team shares a common drive to succeed and a passion for delivering the best. Many of our team have built their careers with us — alongside new talent we mentor from their first step in the industry.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-rs space-y-3">
          {CAREERS.map((r, i) => (
            <Reveal key={r.id} delay={i * 60}>
              <div className="group grid md:grid-cols-12 gap-4 items-center p-6 md:p-8 rounded-2xl bg-white border border-[var(--line)] hover:border-[var(--coral)] transition-colors">
                <div className="md:col-span-5">
                  <div className="font-tag text-[var(--coral)]">{r.type}</div>
                  <div className="font-display text-2xl md:text-3xl mt-2 group-hover:text-[var(--coral)] transition-colors">{r.title}</div>
                </div>
                <div className="md:col-span-4 text-[14px] text-[var(--ink-mute)] leading-relaxed">{r.summary}</div>
                <div className="md:col-span-2 font-tag text-[var(--ink-mute)] flex items-center gap-2"><MapPin size={12} /> {r.location}</div>
                <div className="md:col-span-1 flex md:justify-end">
                  <button onClick={() => apply(r)} className="w-12 h-12 rounded-full bg-[var(--navy)] text-white flex items-center justify-center group-hover:bg-[var(--coral)] transition-colors" aria-label={`Apply for ${r.title}`}><ArrowUpRight size={18} /></button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Apply Form */}
      <section id="apply-form" className="py-24 bg-[var(--bg-soft)]">
        <div className="container-rs grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <span className="section-index"><span className="coral-dot" /> Apply</span>
            <h2 className="font-display text-5xl md:text-6xl mt-4 leading-[0.95]">{active ? active.title : 'Speculative '}<span className="font-display-italic text-[var(--coral)]">application.</span></h2>
            <p className="mt-6 text-[var(--ink-mute)] leading-relaxed max-w-md">Send us a short note and your CV — we read every application. If we think you'd suit Bespoke, we'll be in touch within a week.</p>
          </div>
          <div className="md:col-span-7">
            {!sent ? (
              <form onSubmit={submit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <Field label="Your name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
                  <Field label="Email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required />
                  <Field label="Phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
                  <Field label="Role applying for" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} />
                </div>
                <div>
                  <Label>Cover note</Label>
                  <textarea required rows={6} value={form.cover} onChange={(e) => setForm({...form, cover: e.target.value})} className="w-full bg-transparent border-b border-[var(--ink)]/30 focus:border-[var(--coral)] outline-none py-3 text-[16px] resize-none transition-colors" placeholder="A short paragraph about you and why Bespoke..." />
                </div>
                  <button type="submit" disabled={submitting} className="pill pill-coral"><span>{submitting ? 'Sending…' : 'Send Application'}</span><ArrowUpRight size={14} /></button>
              </form>
            ) : (
              <div className="soft-card p-12">
                <div className="w-14 h-14 rounded-full bg-[var(--coral)]/10 text-[var(--coral)] flex items-center justify-center"><Check /></div>
                <h3 className="font-display text-5xl mt-6">Thank you, {form.name}.</h3>
                <p className="mt-4 text-[var(--ink-mute)] max-w-md">We've received your application. We read every one personally and will reply within a week.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Label({ children }) {
  return <div className="font-tag text-[var(--ink-mute)] mb-3">{children}</div>;
}
function Field({ label, ...props }) {
  return (
    <div>
      <Label>{label}</Label>
      <input {...props} className="w-full bg-transparent border-b border-[var(--ink)]/30 focus:border-[var(--coral)] outline-none py-3 text-[16px] transition-colors" />
    </div>
  );
}
